import React, { memo, useCallback, useMemo } from 'react';
import { createUseStyles, Loader, Markdown, Modal, Text } from '@Sage/hoi-poi-ui';
import { downloadFile } from '@Sage/ssm-utils';

import { useWeb5Context } from 'lib/Web5Context';

import styles from './styles';

const useStyles = createUseStyles(styles, 'CopilotArtifactViewer');

/**
 * Modal that renders the body of a downloadable Copilot artifact (currently
 * markdown). State is owned by `CopilotProvider`, which fetches the artifact
 * payload via `downloadCopilotArtifact` and feeds it here.
 */
const ArtifactViewer = ({ state, onClose }) => {
    const classes = useStyles();
    const { t } = useWeb5Context();

    const { isOpen = false, artifact, content, loading, error } = state || {};

    const title = artifact?.name || artifact?.artifactType || '';
    const isMarkdownArtifact = artifact?.artifactType === 'markdown';

    const downloadFileName = useMemo(() => {
        const baseFileName = artifact?.download?.name || artifact?.name || 'artifact';
        if (baseFileName.endsWith('.md')) return baseFileName;
        return `${baseFileName}.md`;
    }, [artifact]);

    const handleDownload = useCallback(() => {
        if (!isMarkdownArtifact || !content) return;
        downloadFile(content, downloadFileName, 'text/markdown;charset=utf-8');
    }, [content, downloadFileName, isMarkdownArtifact]);

    return (
        <Modal
            classes={{ content: classes.content }}
            confirmText={isMarkdownArtifact ? t('action_download') : undefined}
            isConfirmDisabled={isMarkdownArtifact ? loading || !!error || !content : undefined}
            isOpen={isOpen}
            onConfirm={isMarkdownArtifact ? handleDownload : undefined}
            onRequestClose={onClose}
            overlayClassName={classes.overlay}
            title={title}
        >
            <div className={classes.body}>
                {loading && (
                    <div className={classes.loading}>
                        <Loader />
                    </div>
                )}
                {!loading && error && (
                    <div className={classes.error}>
                        <Text type="body">{t('label_copilot_artifact_load_error') || error}</Text>
                    </div>
                )}
                {!loading && !error && content && (
                    <Markdown
                        className={classes.markdown}
                        content={content}
                        overrides={{ root: { skipHtml: true } }}
                        showTableActions
                    />
                )}
            </div>
        </Modal>
    );
};

ArtifactViewer.displayName = 'CopilotArtifactViewer';

export default memo(ArtifactViewer);
